import React, { useEffect, useRef, useState } from 'react';
import { Scope } from '../data/cache';
import { useScope } from '../hooks/useScope';
import { buttonReset, color, font, radius, shadow, space, weight } from '../theme';
import Icon from './Icon';

// "מכהנת מ-10.11.2026" / "2022–2026", from the Knesset terms in meta/scope
const termLabel = (scope: Scope, k: number): string => {
  const t = scope.knessets[String(k)];
  if (!t) return '';
  const day = (iso: string) => new Date(iso).toLocaleDateString('he-IL', { timeZone: 'Asia/Jerusalem' });
  if (k === scope.knesset || !t.end) return `מכהנת מ-${day(t.start)}`;
  return `${t.start.slice(0, 4)}–${t.end.slice(0, 4)}`;
};

// Which Knesset a list page shows: the current one or an archived one. A pill in the page
// header that opens a short list. Renders nothing while no Knesset is archived.
const KnessetMenu = ({ value, onChange }: { value: number; onChange: (k: number) => void }) => {
  const scope = useScope();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on a click outside or Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const archived = scope.archivedKnessets.filter(k => k !== scope.knesset).sort((a, b) => b - a);
  if (archived.length === 0) return null;
  const options = [scope.knesset].concat(archived);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen(o => !o)}
        style={{
          ...buttonReset, display: 'flex', alignItems: 'center', gap: space.xs + 2, height: 36, padding: `0 ${space.md + 2}px`,
          background: color.surface, boxShadow: shadow.raised, borderRadius: radius.pill,
          fontSize: font.label, fontWeight: weight.medium, color: color.primary,
        }}
      >
        הכנסת ה-{value}
        <Icon name="chevronDown" size={14} strokeWidth={2.4} />
      </button>
      {open && (
        <div role="listbox" aria-label="בחירת כנסת" style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 20, minWidth: 230,
          background: color.surface, borderRadius: radius.lg, boxShadow: shadow.floating, padding: space.xs + 2,
        }}>
          {options.map(k => {
            const selected = k === value;
            return (
              <button
                key={k}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { setOpen(false); if (k !== value) onChange(k); }}
                style={{
                  ...buttonReset, width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: space.sm, padding: `${space.sm + 2}px ${space.md}px`, borderRadius: radius.md,
                  background: selected ? color.primarySoft : 'none',
                }}
              >
                <span style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: font.body, fontWeight: selected ? weight.bold : weight.medium, color: selected ? color.primaryInk : color.text }}>הכנסת ה-{k}</span>
                  <span style={{ fontSize: font.caption, color: color.textSecondary }}>{termLabel(scope, k)}</span>
                </span>
                {selected && <span style={{ color: color.primaryInk, display: 'flex' }}><Icon name="check" size={16} strokeWidth={2.4} /></span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default KnessetMenu;
